import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import _ from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { Container } from "reactstrap";
import { useSocialPostCore } from "../../../../cores/socialPost";
import { SocialProviderId } from "../../../../cores/socialPost/config";
import { IconMapper } from "../selector/SocialPostSelector";
import { FacebookPostPreview } from "./FacebookPostPreview";
import { InstagramPostPreview } from "./InstagramPostPreview";
import { LinkedInPostPreview } from "./LinkedInPostPreview";
import { TwitterPostPreview } from "./TwitterPostPreview";
import { TiktokPostPreview } from "./TiktokPostPreview";
import "./SocialPostPreview.scss";

interface SocialPostPreviewProps {
  /** Tighter layout for embedded panels (e.g. calendar composer) — no full-height stretch */
  compact?: boolean;
}

export const SocialPostPreview = ({ compact = false }: SocialPostPreviewProps) => {
  const intl = useIntl();
  const { postErrors, socialProviders } = useSocialPostCore();
  const [previewId, setPreviewId] = useState<string>("");
  useEffect(() => {
    if ((socialProviders as any[]).length === 0) setPreviewId("");
    if ((socialProviders as any[]).length > 0)
      if (
        !previewId ||
        !(socialProviders as any[]).filter((sp: any) => sp.id === previewId).length
      )
        setPreviewId((socialProviders as any[])[0].id);
  }, [previewId, socialProviders]);

  const showIcon = useCallback(
    (id: string) => {
      const error = (postErrors as any[]).filter(
        ({ socialProviderId }: any) => socialProviderId === id
      );
      return error.length > 0 ? (
        <FontAwesomeIcon
          icon={["fas", "exclamation-circle"]}
          size="xs"
          color="red"
          className="position-absolute"
          style={{ marginLeft: "22px", marginTop: "20px", zIndex: 100 }}
        />
      ) : null;
    },
    [postErrors]
  );

  const showPreview = useMemo(() => {
    switch (previewId) {
      case SocialProviderId.Facebook:
        return <FacebookPostPreview />;
      case SocialProviderId.Twitter:
        return <TwitterPostPreview />;
      case SocialProviderId.LinkedIn:
        return <LinkedInPostPreview />;
      case SocialProviderId.LinkedInBusiness:
        return <LinkedInPostPreview business />;
      case SocialProviderId.Instagram:
        return <InstagramPostPreview />;
      case SocialProviderId.TikTok:
        return <TiktokPostPreview />;
      default:
        return null;
    }
  }, [previewId]);

  // Si no hay redes sociales seleccionadas, mostrar mensaje
  if ((socialProviders as any[]).length === 0) {
    return (
      <Container
        className={`${
          compact ? "" : "h-100"
        } overflow-auto pb-3 mb-1 d-flex align-items-center justify-content-center social-post-preview-empty px-3`}
        style={
          compact
            ? { maxHeight: "min(70vh, 520px)", minHeight: "120px" }
            : { maxHeight: "700px" }
        }
      >
        <div className="text-center w-100">
          <p className="modern-post-empty-hint text-break px-1 mb-0">
            {intl.formatMessage({ id: "app.socialPostSelector.selectNetworkPreview" })}
          </p>
        </div>
      </Container>
    );
  }

  return (
    <Container
      className={`social-post-preview-container ${compact ? "pb-2 mb-0" : "pb-3 mb-1"}`}
    >
      {/* Selector de redes sociales - solo se muestra si hay más de una */}
      {(socialProviders as any[]).length > 1 && (
        <div className="preview-switch-container">
          <div className="preview-switch">
            {(socialProviders as any[]).sort().map(({ id }: any) => (
              <button
                key={id}
                className={`preview-switch-button ${id === previewId ? 'active' : ''}`}
                onClick={() => setPreviewId(id)}
              >
                <div className="switch-icon">
                  {showIcon(id)}
                  {_.isFunction(IconMapper[id]) &&
                    IconMapper[id]({ active: id === previewId, size: "1x" })}
                </div>
                <div className="switch-label">{id}</div>
              </button>
            ))}
          </div>
        </div>
      )}
      {showPreview}
    </Container>
  );
};
